/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductRefinerHierarchy } from '@msdyn365-commerce/commerce-entities';
import { IActionContext } from '@msdyn365-commerce/core';
import { IQueryResultSettings, ProductRefiner, ProductSearchCriteria } from '@msdyn365-commerce/retail-proxy';
import { getProductSearchRefinersAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';

/**
 * Uses Bulk refiner API to get refiners and child refiner values.
 * @param searchCriteria Product search criteria.
 * @param maxItems Max items.
 * @param queryResultSettings
 * @param ctx Action context.
 */
export async function getProductRefinerHierarchy(
    searchCriteria: ProductSearchCriteria,
    queryResultSettings: IQueryResultSettings,
    ctx: IActionContext
): Promise<IProductRefinerHierarchy[]> {

    const publicSearchId = ctx.requestContext.app.config.publicSearchId;
    if (publicSearchId && !ctx.requestContext.user.isAuthenticated) {
        const publicSearch = {
            RefinerRecordId: +publicSearchId,
            RefinerSourceValue: 1,
            DataTypeValue: 6,
            LeftValueBoundString: '1',
            RightValueBoundString: '1',
            UnitText: '',
            RowNumber: 0,
            Count: 0,
            ExtensionProperties: [],
        };

        // IF refiner has existing entries AND does not have any public search refiners, add public search
        if (searchCriteria.Refinement?.length && !searchCriteria.Refinement?.some(refiner =>
            {
                const refinerExists = refiner.RefinerRecordId === publicSearchId;
                return refinerExists;
            }))
        {

        searchCriteria.Refinement.push(publicSearch);
        // IF No refiners exist yet in the Refinement list, add public search
        } else if(!searchCriteria.Refinement?.length) {
            searchCriteria.Refinement = [publicSearch];
        }
    }

    // Search refiner for filtering non-publicly-searchable products when signed out
    return getProductSearchRefinersAsync({ callerContext: ctx, queryResultSettings }, searchCriteria).then(
        (productRefiners: ProductRefiner[]) => {
            if (!productRefiners) {
                ctx.trace('[getRefinersByCriteriaAction] returned no refiners');
                return <IProductRefinerHierarchy[]>[];
            }

            const result = (productRefiners || []).map((productRefiner: ProductRefiner) => <IProductRefinerHierarchy>productRefiner);

            if (result.length === 0) {
                ctx.trace('[getRefinersByCriteriaAction] returned no IProductRefinerHierarchy objects');
                return <IProductRefinerHierarchy[]>[];
            }

            // @CUSTOM sort refiners
            result.forEach(refiner => {
                // Sort the "Values" array based on the "LeftValueBoundString" field
                refiner.Values = refiner.Values.flat().sort((a, b) => {
                    const leftValueA = (a.LeftValueBoundString || '').toLowerCase();
                    const leftValueB = (b.LeftValueBoundString || '').toLowerCase();

                    if (leftValueA < leftValueB) {
                        return -1;
                    }
                    if (leftValueA > leftValueB) {
                        return 1;
                    }
                    return 0;
                });
            });

            return result;
        }
    );
}
